import {
    Order,
    OrderFilterParams,
    OrderSearchParams,
    PlaceOrderBody,
    ScanProductBody,
    Tote,
    UpdateOrderQuantityBody,
} from '@core/models/order.model';
import API from 'services/api';
import { fetchWithRetry } from 'services/fetchWithRetry';

class OrderService {
    private readonly placeOrderURL = `/order/place`;
    private readonly getOrderByIdURL = `/order/get-order`;
    private readonly getOrderForUserURL = `/order/get-order-for-user`;
    private readonly updateOrderURL = `/order/update-order`;
    private readonly createNewToteURL = `/order`;
    private readonly approveToteURL = `/order/totes`;
    private readonly shipToteURL = `/order/totes`;
    private readonly closeToteURL = `/order/totes`;
    private readonly scanProductURL = `/order/totes`;
    private readonly getToteURL = `/order/get-tote`;
    private readonly getToteForUserURL = `/order/get-tote-for-user`;
    private readonly filterOrderURL = `/order/filter-order`;
    private readonly searchOrderURL = `/order/search-order`;
    private readonly exportOrdersURL = `/export/orders`;
    private readonly updateQuantityURL = `/order/update-order/`;
    private readonly cancelOrderURL = `/order/cancel/order-id=`;

    // ---------------------------------------------------------------

    placeOrder(body: PlaceOrderBody) {
        return fetchWithRetry(API.post(this.placeOrderURL, body));
    }

    getOrderById(id: number) {
        return fetchWithRetry(API.get<Order>(`${this.getOrderByIdURL}/${id}`));
    }

    getOrderForUser(id: number) {
        return fetchWithRetry(API.get<Order>(`${this.getOrderForUserURL}/${id}`));
    }

    updateOrderQuantity(orderId: number, body: UpdateOrderQuantityBody[]) {
        return fetchWithRetry(API.patch(`${this.updateOrderURL}/${orderId}`, body));
    }

    createNewTote(orderId: number) {
        return fetchWithRetry(API.post(`${this.createNewToteURL}/${orderId}/new-tote`));
    }

    approveTote(toteId: string) {
        return fetchWithRetry(API.post(`${this.approveToteURL}/${toteId}/approve`));
    }

    shipTote(toteId: number, customerNote?: string) {
        return fetchWithRetry(API.post(`${this.shipToteURL}/${toteId}/ship`, { customerNote }));
    }

    closeTote(toteId: number) {
        return fetchWithRetry(API.post(`${this.closeToteURL}/${toteId}/close`));
    }

    scanProduct(toteId: string, body: ScanProductBody) {
        return fetchWithRetry(API.post(`${this.scanProductURL}/${toteId}/scan`, body));
    }

    getTote(toteId: number) {
        return fetchWithRetry(API.get<Tote>(`${this.getToteURL}/${toteId}`));
    }

    getToteForUser(orderId: number) {
        return fetchWithRetry(API.get<Tote>(`${this.getToteForUserURL}/${orderId}`));
    }

    filterOrder(params?: OrderFilterParams) {
        return fetchWithRetry(API.get(`${this.filterOrderURL}`, { params }));
    }

    searchOrder(params?: OrderSearchParams) {
        return fetchWithRetry(API.get(`${this.searchOrderURL}`, { params }));
    }

    exportOrders(body: { startDate: string; endDate: string }) {
        return fetchWithRetry(API.post(this.exportOrdersURL, body));
    }
    updateQuantityOrder(orderId: number, body: UpdateOrderQuantityBody[]) {
        return fetchWithRetry(API.patch(`${this.updateQuantityURL}${orderId}`, body));
    }
    cancelOrder(orderId: number) {
        return fetchWithRetry(API.post(`${this.cancelOrderURL}${orderId}`));
    }

    getBackorders(params?: { pageNumber?: number; pageSize?: number; status?: string }) {
        return fetchWithRetry(API.get(`/order/backorders`, { params }));
    }

    updateCustomerPoNumber(orderId: number, poNumber: string) {
        return fetchWithRetry(API.patch(`/order/${orderId}/po-number`, { poNumber }));
    }
}

export default new OrderService();
